import React from 'react';
import { X, UserCheck, Check, Crown, CreditCard } from 'lucide-react';

interface VerificationModalProps {
  showVerificationModal: boolean;
  setShowVerificationModal: (show: boolean) => void;
  session: any;
  setShowLoginModal: (show: boolean) => void;
}

const VerificationModal: React.FC<VerificationModalProps> = ({
  showVerificationModal,
  setShowVerificationModal,
  session,
  setShowLoginModal
}) => {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full overflow-hidden">
        {/* Header with colorful gradient */}
        <div className="bg-linear-to-r from-purple-600 via-indigo-600 to-blue-500 py-6 px-8 relative">
          <h2 className="text-2xl font-bold text-white mb-2">Verified Members Feature</h2>
          <p className="text-purple-100">Connect with dedicated study partners in your area</p>
          <button 
            className="absolute top-6 right-6 text-white hover:text-purple-200"
            onClick={() => setShowVerificationModal(false)}
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <div className="p-8">
          {/* Study Partners SVG Illustration */}
          <div className="flex justify-center mb-6">
            <svg width="200" height="160" viewBox="0 0 200 160" className="fill-current">
              <defs>
                <linearGradient id="studyGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#6366F1" />
                  <stop offset="100%" stopColor="#8B5CF6" />
                </linearGradient>
              </defs>
              
              {/* Desk */}
              <rect x="20" y="110" width="160" height="10" rx="2" fill="#94A3B8" />
              <rect x="30" y="120" width="140" height="6" rx="1" fill="#64748B" />
              <rect x="40" y="126" width="120" height="30" rx="1" fill="#475569" />
              
              {/* Books and study materials */}
              <rect x="45" y="85" width="40" height="25" rx="2" fill="#3B82F6" />
              <rect x="48" y="82" width="34" height="3" rx="1" fill="#60A5FA" />
              <rect x="95" y="95" width="30" height="15" rx="2" fill="#EC4899" />
              <rect x="130" y="90" width="25" height="20" rx="2" fill="#14B8A6" />
              
              {/* Person 1 (left) */}
              <circle cx="65" cy="60" r="15" fill="url(#studyGradient)" />
              <path d="M50 105 C 50 85, 65 80, 65 80 C 65 80, 80 85, 80 105" fill="#818CF8" />
              <rect x="55" y="105" width="20" height="15" rx="2" fill="#818CF8" />
              
              {/* Person 2 (right) */}
              <circle cx="135" cy="60" r="15" fill="url(#studyGradient)" />
              <path d="M120 105 C 120 85, 135 80, 135 80 C 135 80, 150 85, 150 105" fill="#C084FC" />
              <rect x="125" y="105" width="20" height="15" rx="2" fill="#C084FC" />
              
              {/* Light bulb / idea symbol */}
              <circle cx="100" cy="30" r="12" fill="#FBBF24" />
              <path d="M100 42 L100 50 M96 53 L104 53" stroke="#FBBF24" strokeWidth="2" />
            </svg>
          </div>
          
          <div className="text-center mb-6">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Upgrade to Access Study Partners</h3>
            <p className="text-gray-600">
              Our study partner matching system connects you with other LSAT students 
              who match your schedule, goals, and learning style.
            </p>
          </div>
          
          <div className="bg-indigo-50 rounded-lg p-6 mb-6">
            <div className="flex items-start space-x-4">
              <div className="bg-indigo-100 p-2 rounded-full">
                <UserCheck className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <h4 className="font-medium text-indigo-900 mb-1">Verified Members Only</h4>
                <p className="text-indigo-700 text-sm">
                  This feature is available exclusively to verified members who have completed
                  at least one practice test with us.
                </p>
              </div>
            </div>
          </div>
          
          <div className="space-y-4 mb-6">
            <div className="flex items-center">
              <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
              <span className="text-gray-700">Get matched with compatible study partners</span>
            </div>
            <div className="flex items-center">
              <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
              <span className="text-gray-700">Schedule regular study sessions</span>
            </div>
            <div className="flex items-center">
              <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
              <span className="text-gray-700">Track progress together and stay accountable</span>
            </div>
            <div className="flex items-center">
              <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
              <span className="text-gray-700">Access to group study rooms and resources</span>
            </div>
          </div>
          
          <div className="bg-linear-to-r from-indigo-100 via-purple-100 to-pink-100 p-6 rounded-lg mb-6">
            <div className="flex items-center mb-3">
              <Crown className="h-5 w-5 text-amber-500 mr-2" />
              <h4 className="font-medium text-gray-900">Verified Membership</h4>
            </div>
            <div className="flex justify-between items-center">
              <div>
                <p className="text-gray-600 mb-1">Starting at</p>
                <p className="text-2xl font-bold text-gray-900">$19.99<span className="text-gray-600 text-sm font-normal">/month</span></p>
              </div>
              <div>
                <p className="text-indigo-600 text-sm">Includes:</p>
                <ul className="text-xs text-gray-600">
                  <li>• Practice tests</li>
                  <li>• Study groups</li>
                  <li>• Premium resources</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="flex space-x-4">
            <button 
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              onClick={() => setShowVerificationModal(false)}
            >
              Not Now
            </button>
            <button 
              className="flex-1 px-4 py-2 bg-linear-to-r from-indigo-600 to-purple-600 text-white rounded-md hover:from-indigo-700 hover:to-purple-700 transition-colors flex items-center justify-center"
              onClick={() => {
                setShowVerificationModal(false);
                if (!session) setShowLoginModal(true);
              }}
            >
              <CreditCard className="mr-2 h-4 w-4" />
              Upgrade Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerificationModal;
